'use client';

import classNames from 'classnames';
import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { logoutCustomer } from '@/apis/tds';
import { doTranslation } from '@/app/i18n/client';
import { useCustomerDetail } from '@/context/customer-detail-context';
import { localeSpecificPath } from '@/utils/localeUtils';

import styles from '@/components/global/navigations/header.module.scss';

import chevronLightDown from '@/public/assets/images/global/chevron-light-down.svg';
import chevronLightUp from '@/public/assets/images/global/chevron-light-up.svg';
import closeLight from '@/public/assets/images/global/close-light.svg';
import createAccount from '@/public/assets/images/global/navigations/create-account.svg';
import hamburger from '@/public/assets/images/global/navigations/hamburger.svg';
import myAccount from '@/public/assets/images/global/navigations/my-account.svg';
import signIn from '@/public/assets/images/global/navigations/sign-in.svg';
import signOut from '@/public/assets/images/global/navigations/sign-out.svg';
import trailwaysLarge from '@/public/assets/images/global/trailways-large.svg';
import trailwaysSmall from '@/public/assets/images/global/trailways-small.svg';

interface Link {
  href: string;
  target?: string;
  title: string;
  id: string;
}

interface Group {
  id: string;
  title: string;
  gtmClass: string;
  links: Link[];
}

interface Props extends React.AllHTMLAttributes<HTMLDivElement> {
  trailways?: string;
  groups?: Group[];
  countryLanguageSelector?: React.ReactElement;
  globalConfig?: any;
}

const Header = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const nav = useRef<HTMLElement>(null);
  const groups = useRef<HTMLElement[]>([]);
  const [isSignedIn, setIsSignedIn] = useState<null | true | false>(null);
  const [signInURL, setSignInURL] = useState('/account/login/');
  const { customer } = useCustomerDetail();
  const [status, setStatus] = useState({ nav: '', hamburger: false });
  const { t } = doTranslation(lng);

  const logout = async () => {
    await logoutCustomer();
    localStorage.setItem('logoutEvent', 'true');
    clearSessionDataOnLogout();
  };

  const setData = (key: string, data: any) => {
    sessionStorage.setItem(key, JSON.stringify(data));
  };

  const resetTrip = () => {
    setData('scheduleResults', null);
    setData('tripDetailsForm', null);
    setData('itinerary', null);
    resetTripPartial();
  };

  const resetTripPartial = () => {
    setData('charges', null);
    setData('ticketCartGTM', null);
    setData('paymentInfo', null);
    setData('protectionSelected', null);
    setData('kountSessionId', null);
    setData('bookingInfo', null);
    setData('isUsingCredits', false);
    setData('isUsingLoyalty', false);
    setData('promoCode', null);
    setData('loyaltyAmountForBooking', null);
    setData('reservationDetails', null);
  };

  const clearSessionDataOnLogout = () => {
    setData('loginInfo', null);
    setData('storedCards', null);
    setData('saveCard', false);
    setData('guestInfo', null);
    setData('purchaseLoginStep', false);
    setData('userCheckedOut', false);
    const path = window.location.pathname;
    if (
      path.includes('/purchase/passengers') ||
      path.includes('/purchase/payments') ||
      path.includes('/purchase/review')
    ) {
      resetTripPartial();
      // Redirect to /purchase/login
      window.location.href = localeSpecificPath('/purchase/login', lng);
    } else {
      resetTrip();
      window.location.href = localeSpecificPath('/', lng);
    }
  };

  const defaultGroups = [
    {
      id: 'book-now',
      title: t('Plan a Bus Trip'),
      gtmClass: 'gtm-main_menu_plan_a_bus_trip',
      links: [
        { href: '/', title: t('Buy a Bus Ticket'), id: 'gtm-main_menu_buy_a_bus_ticket' },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/charter-bus-rental/`,
          title: t('Charter/Bus Rental'),
          id: 'gtm-main_menu_charter_bus_rental'
        },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/packages/`,
          title: t('Trip Packages'),
          id: 'gtm-main_menu_trip_packages'
        }
      ]
    },
    {
      id: 'travel-info',
      title: t('Travel Info'),
      gtmClass: 'gtm-main_menu_travel_info',
      links: [
        { href: localeSpecificPath('/schedules/', lng), title: t('Bus Schedules'), id: 'gtm-main_menu_bus_schedules' },
        {
          href: localeSpecificPath('/bus-station/', lng),
          title: t('Terminal Locations'),
          id: 'gtm-main_menu_bus_station'
        },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/${localeSpecificPath('service-advisories/', lng)}`,
          title: t('Service Advisories'),
          id: 'gtm-main_menu_service_advisories'
        },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/${localeSpecificPath('international-travel/', lng)}`,
          title: t('International Travel'),
          id: 'gtm-main_menu_international_travel'
        },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/${localeSpecificPath('veteran-honors/', lng)}`,
          title: t('Veteran Honors'),
          id: 'gtm-main_menu_veteran_honors'
        }
      ]
    },
    {
      id: 'customer_service',
      title: t('Customer Service'),
      gtmClass: 'gtm-main_menu_customer_service',
      links: [
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/my-driven-rewards/`,
          title: t('My Driven Rewards'),
          id: 'gtm-main_menu_my_driven_rewards'
        },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/lost-and-found/`,
          title: t('Lost and Found'),
          id: 'gtm-main_menu_lost_and_found'
        },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/faq/`,
          title: t('Travel Policies and FAQs'),
          id: 'gtm-main_menu_travel_policies'
        },
        {
          href: `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/contacts/`,
          title: t('Contact Us'),
          id: 'gtm-main_menu_contact_us'
        }
      ]
    }
  ];
  const headerGroups = props.groups ? props.groups : defaultGroups;

  useEffect(() => {
    if (customer) {
      setIsSignedIn(true);
    } else {
      setIsSignedIn(false);
    }
    setSignInURL(getAccountUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const debounce = (f: Function, delay: number) => {
      let timeout: ReturnType<typeof setTimeout>;
      return function (this: any, ...args: any[]) {
        clearTimeout(timeout);
        timeout = setTimeout(() => f.apply(this, args), delay);
      };
    };

    const handleClick = (event: any) => {
      if (nav.current && !nav.current.contains(event.target)) {
        setStatus((status) => {
          if (!status.hamburger) {
            return { ...status, nav: '' };
          }
          return { ...status };
        });
      }
    };

    const handleKeyUp = (event: any) => {
      switch (event.keyCode) {
        case 9:
          if (nav.current && !nav.current.contains(event.target)) {
            setStatus((status) => {
              if (!status.hamburger) {
                return { ...status, nav: '' };
              }
              return { ...status };
            });
          }
          break;
        case 27:
          setStatus((status) => {
            if (!status.hamburger) {
              return { ...status, nav: '' };
            }
            return { ...status };
          });
          break;
        case 37:
        case 38:
        case 39:
        case 40:
          if (groups.current.length) {
            const group = groups.current.find((ref: HTMLElement) => ref && ref.contains(event.target));
            if (group) {
              const buttons = groups.current
                .map((ref: HTMLElement) => ref.querySelector<HTMLElement>('button[aria-expanded=false]'))
                .filter((ref: HTMLElement | null) => ref);
              const items =
                buttons.length === (headerGroups || []).length
                  ? buttons
                  : group.querySelectorAll<HTMLElement>('ul li a');
              if (event.keyCode === 37 || event.keyCode === 38) {
                items[items.length - 1]?.focus();
                items.forEach((item: HTMLElement | null, index: number) => {
                  if (item === event.target && index > 0) {
                    items[index - 1]?.focus();
                  }
                });
              }
              if (event.keyCode === 39 || event.keyCode === 40) {
                items[0]?.focus();
                items.forEach((item: HTMLElement | null, index: number) => {
                  if (item === event.target && index < items.length - 1) {
                    items[index + 1]?.focus();
                  }
                });
              }
            }
          }
          break;
      }
    };

    const handleResize = () => {
      setStatus({ ...status, hamburger: false });
    };

    const handleLogoutEvent = (event: StorageEvent) => {
      if (event.key === 'logoutEvent') {
        clearSessionDataOnLogout();
      }
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('resize', debounce(handleResize, 250), true);
    window.addEventListener('storage', handleLogoutEvent);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('resize', debounce(handleResize, 250));
      window.removeEventListener('storage', handleLogoutEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const schedule = document.querySelector('.hide-menu');
    if (status.hamburger) {
      document.body.classList.add('no-scroll');
      schedule?.classList.add('hide');
    } else {
      document.body.classList.remove('no-scroll');
      schedule?.classList.remove('hide');
    }
  }, [status]);

  const isPurchaseInProgress = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    const purchasePaths = ['/purchase/passengers', '/purchase/payments', '/purchase/review'];
    return purchasePaths.some((path) => window.location.pathname.includes(path));
  };

  const getAccountUrl = () => {
    let returnPath = null;
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      returnPath = currentPath === '/' ? null : currentPath;
    }
    let loginPath = '/account/login/';
    if (returnPath !== null) {
      loginPath += `?returnUrl=${returnPath}`;
    }
    return localeSpecificPath(loginPath, lng);
  };

  const isAccountPage = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    const pathname = window.location.pathname;
    return (
      pathname.includes('/account/') &&
      !pathname.includes('/account/forgotpassword') &&
      !pathname.includes('/account/login') &&
      !pathname.includes('/account/createaccount')
    );
  };

  return (
    <>
      <header className={styles.header}>
        {isPurchaseInProgress() && (
          <a className="visually-hidden" href="#modify-trip">
            {t('Skip to Modify This Trip')}
          </a>
        )}
        {isAccountPage() && (
          <a className="visually-hidden" href="#account-navigation">
            {t('Skip to My Account Navigation')}
          </a>
        )}
        <a className="visually-hidden" href="#main-content">
          {t('Skip to Main Content')}
        </a>
        <div className={styles.box}>
          <a className={styles.a} href={props.trailways}>
            <Image
              alt={t('Trailways Home Page')}
              className={styles.large}
              height="40"
              src={trailwaysLarge}
              width="168"
            />
            <Image
              alt={t('Trailways Home Page')}
              className={styles.small}
              height="32"
              src={trailwaysSmall}
              width="32"
            />
          </a>
          {!status.hamburger && (
            <nav aria-label={t('Main Menu')} ref={nav}>
              <ul className={styles.ul}>
                {(headerGroups || []).map((group: Group, index: number) => {
                  const id = group.id.replace('group-', '');
                  return (
                    <li
                      className={classNames({ [styles.group]: true, [styles.active]: status.nav === group.id })}
                      key={group.id}
                      ref={(element: HTMLLIElement) => {
                        groups.current[index] = element;
                      }}
                    >
                      <button
                        aria-controls={`main-menu-links-${id}`}
                        aria-expanded={status.nav === group.id}
                        aria-haspopup="menu"
                        className={`${styles.button} ${group.gtmClass}`}
                        id={`nav-${id}-large`}
                        onClick={() => setStatus({ ...status, nav: status.nav !== group.id ? group.id : '' })}
                      >
                        {group.title}
                      </button>
                      {status.nav === group.id && (
                        <ul aria-labelledby={`nav-${id}-large`} className={styles.ul} id={`main-menu-links-${id}`}>
                          {status.nav === group.id &&
                            group.links.map((link: Link) => {
                              return (
                                <li className={styles.li} key={`${link.href}-${link.title}`}>
                                  <a
                                    className={styles.a}
                                    href={link.href}
                                    target={link.target ? link.target : '_self'}
                                    id={link.id}
                                  >
                                    {link.title}
                                    {link.target && link.target !== '_self' && (
                                      <span className="sr-only">{t('opens in a new tab')}</span>
                                    )}
                                  </a>
                                </li>
                              );
                            })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          )}
        </div>
        {!status.hamburger && (
          <nav aria-label={t('Secondary Menu')}>
            <ul className={styles.menu}>
              {customer !== null && (
                <>
                  <li className={styles.li}>
                    <a
                      className={`${styles.a} gtm-my_account`}
                      href={`${localeSpecificPath('/account/summary/', lng)}`}
                    >
                      <Image alt={t('My Account')} aria-hidden="true" height="32" src={myAccount} width="32" />
                      {t('My Account')}
                    </a>
                  </li>
                  <li className={styles.li}>
                    <button
                      className={styles.a}
                      id="gtm-secondary_menu_sign_out"
                      onClick={async () => {
                        await logout();
                      }}
                    >
                      {t('Sign Out')}
                    </button>
                  </li>
                </>
              )}
              {customer === null && (
                <>
                  <li className={styles.li}>
                    <a
                      className={`${styles.a} gtm-secondary_menu_create_account`}
                      href={`${localeSpecificPath('/account/createaccount', lng)}`}
                      id="gtm-secondary_menu_create_account"
                    >
                      {t('Create Account')}
                    </a>
                  </li>
                  <li className={styles.li}>
                    <a className={`${styles.a} gtm-sign_in`} href={signInURL} id="gtm-secondary_menu_sign_in">
                      {t('Sign In')}
                    </a>
                  </li>
                </>
              )}
              {props.countryLanguageSelector}
            </ul>
          </nav>
        )}
        <button
          className={styles.hamburger_source}
          onClick={() => setStatus({ ...status, hamburger: true })}
          type="button"
        >
          <Image alt={t('Hamburger')} height="32" src={hamburger} width="32" />
        </button>
        {status.hamburger && (
          <div className={classNames({ [styles.hamburger_target]: true, [styles.active]: status.hamburger })}>
            <div className={styles.top}>
              <a className={styles.a} href={props.trailways}>
                <Image alt={t('Trailways Home Page')} height="32" src={trailwaysSmall} width="32" />
              </a>
              <button
                className={styles.button}
                onClick={() => setStatus({ ...status, hamburger: false })}
                type="button"
              >
                <Image alt={t('Close')} height="32" src={closeLight} width="32" />
              </button>
            </div>
            <div className={styles.bottom}>
              <div className={styles.first}>
                <nav aria-label={t('Main Menu')}>
                  <ul className={styles.ul}>
                    {(headerGroups || []).map((group: Group) => {
                      const id = group.id.replace('group-', '');
                      return (
                        <li
                          className={classNames({ [styles.group]: true, [styles.active]: status.nav === group.id })}
                          key={group.id}
                        >
                          <button
                            aria-controls={`main-menu-links-${id}`}
                            aria-expanded={status.nav === group.id}
                            aria-haspopup="menu"
                            className={`${styles.button} ${group.gtmClass}`}
                            id={`nav-${id}-small`}
                            onClick={() => setStatus({ ...status, nav: status.nav !== group.id ? group.id : '' })}
                          >
                            {group.title}
                            <Image
                              alt={t('down arrow')}
                              className={styles.down}
                              height="17"
                              src={chevronLightDown}
                              width="17"
                            />
                            <Image
                              alt={t('up arrow')}
                              className={styles.up}
                              height="17"
                              src={chevronLightUp}
                              width="17"
                            />
                          </button>
                          {status.nav === group.id && (
                            <ul aria-labelledby={`nav-${id}-small`} className={styles.ul} id={`main-menu-links-${id}`}>
                              {group.links.map((link: Link) => {
                                return (
                                  <li className={styles.li} key={`${link.href}-${link.title}`}>
                                    <a
                                      className={styles.a}
                                      href={link.href}
                                      target={link.target ? link.target : '_self'}
                                      title={link.title}
                                      id={link.id}
                                    >
                                      {link.title}
                                      {link.target && link.target !== '_self' && (
                                        <span className="sr-only">{t('opens in a new tab')}</span>
                                      )}
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </div>
              <div className={styles.second}></div>
              <div className={styles.third}>
                <div className={styles.block}>{props.countryLanguageSelector}</div>
                <div className={styles.block}>
                  <nav aria-label={t('Secondary Menu')}>
                    {customer !== null && (
                      <ul className={styles.ul}>
                        <li className={styles.li}>
                          <a className={styles.a} href={`${localeSpecificPath('/account/summary/', lng)}`}>
                            <Image alt={t('My Account')} height="32" src={myAccount} width="32" />
                            {t('My Account')}
                          </a>
                        </li>
                        <li className={styles.li}>
                          <button
                            className={styles.a}
                            onClick={async () => {
                              await logout();
                            }}
                            id="gtm-secondary_menu_sign_out"
                          >
                            <Image alt={t('Sign Out')} height="32" src={signOut} width="32" />
                            {t('Sign Out')}
                          </button>
                        </li>
                      </ul>
                    )}
                    {customer === null && (
                      <ul className={styles.ul}>
                        <li className={styles.li}>
                          <a
                            className={styles.a}
                            href={`${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/account/createaccount`}
                            id="gtm-secondary_menu_create_account"
                          >
                            <Image alt={t('Create Account')} height="32" src={createAccount} width="32" />
                            {t('Create Account')}
                          </a>
                        </li>
                        <li className={styles.li}>
                          <a className={styles.a} id="gtm-secondary_menu_sign_in" href={signInURL}>
                            <Image alt={t('Sign In')} height="32" src={signIn} width="32" />
                            {t('Sign In')}
                          </a>
                        </li>
                      </ul>
                    )}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
